import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Globe, ExternalLink, Activity } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { skillPaths } from '../data/learningContent';
import { usePlan } from '../lib/usePlan';
import { toast } from 'sonner';
import { supabase } from '../lib/supabaseClient';
import ToolSwitcher from '../components/ToolSwitcher';
import { getTool } from '../data/tools';
import SkillScorePanel from '../components/SkillScorePanel';
import { computeOverallScore } from '../data/skillAreas';

const PLAN_AI_LIMITS: Record<string, number> = { free: 5, starter: 50, pro: 500 };

const PLAN_LABEL: Record<string, string> = {
  free: 'Inactive',
  starter: 'Starter',
  pro: 'Pro',
};

export default function SettingsPage() {
  const { user, profile, updateProfile, signOut } = useAuth();
  const plan = usePlan();
  const [fullName, setFullName] = useState(profile?.full_name || '');
  const [role, setRole] = useState(profile?.role || '');
  const [readingLevel, setReadingLevel] = useState<'standard' | 'simple'>(profile?.reading_level || 'standard');
  const [weekly, setWeekly] = useState(profile?.weekly_goal_minutes || 60);
  const [handle, setHandle] = useState(profile?.public_handle || '');
  const [bio, setBio] = useState(profile?.public_bio || '');
  const [publicEnabled, setPublicEnabled] = useState(profile?.public_profile_enabled || false);
  const [saving, setSaving] = useState(false);
  const [savingPublic, setSavingPublic] = useState(false);
  const [openingPortal, setOpeningPortal] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancelConfirm, setCancelConfirm] = useState(false);
  const [cancelAtPeriodEnd, setCancelAtPeriodEnd] = useState(false);
  const [aiUsage, setAiUsage] = useState<{ today: number; month: number }>({ today: 0, month: 0 });
  const [skillScores, setSkillScores] = useState<Record<string, number>>({});
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deletePhrase, setDeletePhrase] = useState('');
  const [deleting, setDeleting] = useState(false);

  const deleteAccount = async () => {
    if (deletePhrase !== 'DELETE') {
      toast.error('Type DELETE to confirm');
      return;
    }
    setDeleting(true);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData.session?.access_token;
      if (!accessToken) {
        toast.error('Please sign in again');
        return;
      }
      const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/delete-account`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ confirm: 'DELETE' }),
      });
      const json = await res.json();
      if (!res.ok) {
        toast.error(json.error || 'Could not delete account');
        return;
      }
      toast.success('Account deleted');
      await signOut();
      window.location.href = '/';
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setDeleting(false);
    }
  };

  const openBillingPortal = async () => {
    setOpeningPortal(true);
    const portalWindow = window.open('', '_blank');
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData.session?.access_token;
      if (!accessToken) {
        portalWindow?.close();
        toast.error('Please sign in again');
        return;
      }
      const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/stripe-portal`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ return_url: window.location.href }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || !json.url) {
        portalWindow?.close();
        toast.error(json.error || 'Could not open billing portal. You can still cancel below.', { duration: 6000 });
        return;
      }
      if (portalWindow) {
        portalWindow.location.href = json.url;
      } else {
        window.location.href = json.url;
      }
    } catch (e) {
      portalWindow?.close();
      toast.error((e as Error).message);
    } finally {
      setOpeningPortal(false);
    }
  };

  const cancelSubscription = async (action: 'cancel' | 'reactivate') => {
    setCancelling(true);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData.session?.access_token;
      if (!accessToken) {
        toast.error('Please sign in again');
        return;
      }
      const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/stripe-cancel`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ action }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error(json.error || 'Could not update subscription');
        return;
      }
      setCancelAtPeriodEnd(action === 'cancel');
      toast.success(
        action === 'cancel'
          ? 'Subscription set to cancel at the end of this period'
          : 'Subscription resumed',
      );
      setCancelConfirm(false);
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setCancelling(false);
    }
  };

  useEffect(() => {
    if (profile) {
      setFullName(profile.full_name || '');
      setRole(profile.role || '');
      setWeekly(profile.weekly_goal_minutes || 60);
      setHandle(profile.public_handle || '');
      setBio(profile.public_bio || '');
      setPublicEnabled(profile.public_profile_enabled || false);
    }
  }, [profile]);

  useEffect(() => {
    if (!user) return;
    (async () => {
      const { data: usage } = await supabase.rpc('get_ai_usage_today');
      const row = Array.isArray(usage) ? usage[0] : usage;
      if (row) setAiUsage({ today: row.calls_today || 0, month: row.calls_this_month || 0 });
      const { data: scoreRows } = await supabase.from('skill_scores').select('skill_area, score').eq('user_id', user.id);
      const scores: Record<string, number> = {};
      (scoreRows || []).forEach((r: any) => { scores[r.skill_area || r.dimension] = r.score; });
      setSkillScores(scores);
    })();
  }, [user]);

  useEffect(() => {
    if (!user || plan.plan === 'free') {
      setCancelAtPeriodEnd(false);
      return;
    }
    (async () => {
      const { data } = await supabase
        .from('stripe_user_subscriptions')
        .select('cancel_at_period_end')
        .maybeSingle();
      setCancelAtPeriodEnd(!!data?.cancel_at_period_end);
    })();
  }, [user, plan.plan]);

  const save = async () => {
    setSaving(true);
    await updateProfile({ full_name: fullName, role, weekly_goal_minutes: weekly, reading_level: readingLevel });
    setSaving(false);
    toast.success('Settings saved');
  };

  const savePublic = async () => {
    if (!user) return;
    if (!plan.hasPublicProfile) {
      toast.error('Public profiles are a Pro feature');
      return;
    }
    setSavingPublic(true);
    const cleaned = handle.toLowerCase().replace(/[^a-z0-9-]/g, '').slice(0, 30);
    const { error } = await supabase
      .from('profiles')
      .update({
        public_handle: cleaned || null,
        public_bio: bio,
        public_profile_enabled: publicEnabled,
      })
      .eq('id', user.id);
    setSavingPublic(false);
    if (error) {
      toast.error(error.message.includes('duplicate') ? 'That handle is taken' : 'Could not save');
      return;
    }
    setHandle(cleaned);
    toast.success('Public profile updated');
  };

  return (
    <div className="container-app py-12 max-w-3xl">
      <div className="mb-10">
        <div className="text-xs uppercase tracking-[0.22em] text-ink-500 mb-3">Settings</div>
        <h1 className="font-display text-5xl">Your account.</h1>
      </div>

      <div className="card p-7 mb-5">
        <h3 className="font-display text-xl mb-5">Profile</h3>
        <div className="space-y-4">
          <div>
            <label className="field-label block mb-1.5">Email</label>
            <div className="text-ink-700 text-sm">{user?.email}</div>
          </div>
          <div>
            <label className="field-label block mb-1.5">Full name</label>
            <input className="input" value={fullName} onChange={(e) => setFullName(e.target.value)} />
          </div>
          <div>
            <label className="field-label block mb-1.5">Practice path</label>
            <select className="input" value={skillPaths.some((p) => p.slug === role) ? role : ''} onChange={(e) => setRole(e.target.value)}>
              <option value="">{role && !skillPaths.some((p) => p.slug === role) ? 'Pick a new path' : 'Pick a path'}</option>
              {skillPaths.map((p) => <option key={p.slug} value={p.slug}>{p.title.replace('AI for ', '')}</option>)}
            </select>
            {role && !skillPaths.some((p) => p.slug === role) && (
              <p className="text-xs text-ink-500 mt-1.5">Our paths were updated. Choose a new one to refresh your dashboard.</p>
            )}
          </div>
          <div>
            <label className="field-label block mb-1.5">Weekly learning goal</label>
            <select className="input" value={weekly} onChange={(e) => setWeekly(parseInt(e.target.value))}>
              <option value={30}>30 minutes / week</option>
              <option value={60}>1 hour / week</option>
              <option value={120}>2 hours / week</option>
              <option value={240}>4 hours / week</option>
            </select>
          </div>
        </div>
        <button onClick={save} disabled={saving} className="btn-primary mt-6 disabled:opacity-50">
          {saving ? 'Saving...' : 'Save changes'}
        </button>
      </div>

      <div className="card p-7 mb-5">
        <h3 className="font-display text-xl mb-2">Default AI tool</h3>
        <p className="text-sm text-ink-600 mb-4">Pick the assistant you primarily use. Lessons format their output to match {getTool(profile?.preferred_tool).name}.</p>
        <ToolSwitcher size="md" />
      </div>

      <div className="card p-7 mb-5">
        <h3 className="font-display text-xl mb-2">Reading level</h3>
        <p className="text-sm text-ink-600 mb-4">
          Pick how lessons and the coach are written. Switch any time — the lesson shape stays the
          same, only the language changes.
        </p>
        <div className="grid sm:grid-cols-2 gap-3">
          <button
            onClick={() => setReadingLevel('standard')}
            className={`text-left p-4 rounded-xl border transition ${
              readingLevel === 'standard' ? 'border-accent-500 bg-warm-50' : 'border-ink-100 hover:border-ink-200'
            }`}
          >
            <div className="font-medium mb-0.5">Standard</div>
            <div className="text-xs text-ink-500">Full sentences, normal vocabulary.</div>
          </button>
          <button
            onClick={() => setReadingLevel('simple')}
            className={`text-left p-4 rounded-xl border transition ${
              readingLevel === 'simple' ? 'border-accent-500 bg-warm-50' : 'border-ink-100 hover:border-ink-200'
            }`}
          >
            <div className="font-medium mb-0.5">Plain language</div>
            <div className="text-xs text-ink-500">Short sentences, lighter vocabulary. Good for kids and ESL learners.</div>
          </button>
        </div>
      </div>

      <div className="card p-7 mb-5">
        <h3 className="font-display text-xl mb-4">Skill Profile</h3>
        <SkillScorePanel scores={skillScores} overallScore={computeOverallScore(skillScores)} />
      </div>

      <div className="card p-7 mb-5">
        <div className="flex items-start justify-between gap-4 mb-2">
          <h3 className="font-display text-xl">Subscription</h3>
          <span className="chip-accent text-xs">{PLAN_LABEL[plan.plan] || 'Free'}</span>
        </div>
        {plan.plan === 'free' ? (
          <div>
            <p className="text-sm text-ink-600 mb-4">You are on Free with module one of every path unlocked and the AI Coach available up to five conversations a day. Upgrade when you want the rest of the curriculum or more headroom.</p>
            <Link to="/pricing" className="btn-primary">Compare plans</Link>
          </div>
        ) : (
          <div>
            <p className="text-sm text-ink-600 mb-2">
              You are on {plan.plan === 'starter' ? 'Starter' : 'Pro'}.
              {plan.plan === 'starter' ? ' Module one of every path is unlocked.' : ' Every path is unlocked.'}
              {plan.planRenewsAt && (
                cancelAtPeriodEnd
                  ? ` Cancels on ${new Date(plan.planRenewsAt).toLocaleDateString()}.`
                  : ` Renews ${new Date(plan.planRenewsAt).toLocaleDateString()}.`
              )}
            </p>
            {plan.plan === 'starter' && !cancelAtPeriodEnd && (
              <p className="text-sm text-ink-600 mb-4">Upgrade to Pro for every module of every path, plain-language reading mode, completion certificates, and a public learner profile.</p>
            )}
            {cancelAtPeriodEnd && (
              <div className="rounded-xl bg-amber-50 border border-amber-200 p-4 mb-4 text-sm text-amber-900">
                Your subscription is set to cancel at the end of this period. Reactivate any time to keep your access without interruption.
              </div>
            )}
            <div className="flex gap-2 flex-wrap">
              {plan.plan === 'starter' && !cancelAtPeriodEnd && (
                <Link to="/pricing" className="btn-primary text-sm">Upgrade to Pro</Link>
              )}
              <button onClick={openBillingPortal} disabled={openingPortal} className={plan.plan === 'pro' ? 'btn-primary text-sm disabled:opacity-50' : 'btn-secondary text-sm disabled:opacity-50'}>
                <ExternalLink className="w-4 h-4" /> {openingPortal ? 'Opening...' : 'Manage billing'}
              </button>
              <Link to="/pricing" className="btn-ghost text-sm">Change plan</Link>
              {cancelAtPeriodEnd ? (
                <button
                  onClick={() => cancelSubscription('reactivate')}
                  disabled={cancelling}
                  className="btn-secondary text-sm disabled:opacity-50"
                >
                  {cancelling ? 'Working…' : 'Reactivate subscription'}
                </button>
              ) : (
                <button
                  onClick={() => setCancelConfirm(true)}
                  className="btn-ghost text-sm text-rose-700 hover:text-rose-900"
                >
                  Cancel subscription
                </button>
              )}
            </div>
            <p className="text-xs text-ink-500 mt-3">
              Manage billing opens the Stripe customer portal in a new tab. If your browser blocks it,
              use Cancel subscription here instead.
            </p>
          </div>
        )}
      </div>

      {cancelConfirm && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-ink-900/40 backdrop-blur-sm px-6">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-lift">
            <h3 className="font-display text-2xl mb-2">Cancel your subscription?</h3>
            <p className="text-sm text-ink-600 mb-5">
              You will keep access until {plan.planRenewsAt ? new Date(plan.planRenewsAt).toLocaleDateString() : 'the end of your current period'}.
              After that, your account stays — you just drop back to Free.
            </p>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setCancelConfirm(false)} className="btn-ghost text-sm" disabled={cancelling}>
                Keep subscription
              </button>
              <button
                onClick={() => cancelSubscription('cancel')}
                disabled={cancelling}
                className="btn-primary text-sm disabled:opacity-50"
              >
                {cancelling ? 'Cancelling…' : 'Yes, cancel at period end'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="card p-7 mb-5">
        <div className="flex items-start justify-between gap-4 mb-2">
          <div>
            <h3 className="font-display text-xl">Public notebook</h3>
            <p className="text-sm text-ink-600">A shareable URL for the reflections you choose to make public.</p>
          </div>
          {!plan.hasPublicProfile && <span className="chip text-xs">Pro feature</span>}
        </div>
        <div className="space-y-4 mt-4">
          <div>
            <label className="field-label block mb-1.5">Handle</label>
            <div className="flex items-center gap-2">
              <span className="text-sm text-ink-500">{window.location.host}/u/</span>
              <input
                className="input"
                placeholder="your-handle"
                value={handle}
                disabled={!plan.hasPublicProfile}
                onChange={(e) => setHandle(e.target.value)}
              />
            </div>
          </div>
          <div>
            <label className="field-label block mb-1.5">Bio</label>
            <textarea
              className="textarea text-sm"
              rows={3}
              placeholder="What are you learning?"
              value={bio}
              disabled={!plan.hasPublicProfile}
              onChange={(e) => setBio(e.target.value)}
            />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={publicEnabled}
              disabled={!plan.hasPublicProfile}
              onChange={(e) => setPublicEnabled(e.target.checked)}
            />
            Make my notebook publicly visible
          </label>
          <div className="flex items-center gap-2">
            <button onClick={savePublic} disabled={savingPublic || !plan.hasPublicProfile} className="btn-primary disabled:opacity-50">
              {savingPublic ? 'Saving...' : 'Save profile'}
            </button>
            {publicEnabled && handle && plan.hasPublicProfile && (
              <Link to={`/u/${handle}`} className="btn-secondary inline-flex items-center gap-2 text-sm">
                <Globe className="w-4 h-4" /> View public notebook
              </Link>
            )}
          </div>
        </div>
      </div>

      <div className="card p-7 mb-5">
        <div className="flex items-center gap-2 mb-4">
          <Activity className="w-4 h-4 text-ink-500" />
          <h3 className="font-display text-xl">AI usage this month</h3>
        </div>
        {(() => {
          if (plan.plan === 'pro') {
            return (
              <div>
                <div className="font-display text-3xl">{aiUsage.today} <span className="text-ink-300 text-xl">today</span></div>
                <div className="text-sm text-ink-500">{aiUsage.month} calls this month — Pro is unmetered for everyday learning.</div>
              </div>
            );
          }
          const limit = PLAN_AI_LIMITS[plan.plan] || PLAN_AI_LIMITS.free;
          const pct = Math.min(100, Math.round((aiUsage.today / limit) * 100));
          const warn = pct >= 80;
          return (
            <div>
              <div className="flex items-end justify-between mb-2">
                <div>
                  <div className="font-display text-3xl">{aiUsage.today}<span className="text-ink-300 text-xl"> / {limit} today</span></div>
                  <div className="text-sm text-ink-500">{aiUsage.month} calls this month</div>
                </div>
                {warn && <span className="chip text-xs text-amber-700 bg-amber-50">{pct}% used</span>}
              </div>
              <div className="progress-track"><div className={`progress-fill ${warn ? 'bg-amber-500' : ''}`} style={{ width: `${pct}%` }} /></div>
              <p className="text-xs text-ink-500 mt-3">Upgrade to Pro for unmetered AI Coach.</p>
            </div>
          );
        })()}
      </div>

      <div className="card p-7">
        <h3 className="font-display text-xl mb-2">Sign out</h3>
        <p className="text-sm text-ink-600 mb-4">You can sign back in any time.</p>
        <button onClick={signOut} className="btn-secondary">Sign out</button>
      </div>

      <div className="card p-7 border-rose-200 bg-rose-50/40">
        <h3 className="font-display text-xl mb-2 text-rose-900">Delete account</h3>
        <p className="text-sm text-ink-600 mb-4">
          Permanently remove your account, profile, reflections, and lesson history.
          This cannot be undone. If you have an active subscription, cancel it from billing first.
        </p>
        {!deleteOpen ? (
          <button
            onClick={() => setDeleteOpen(true)}
            className="btn-secondary text-rose-700 border-rose-200 hover:bg-rose-100"
          >
            Delete my account
          </button>
        ) : (
          <div className="space-y-3">
            <label className="field-label block">Type DELETE to confirm</label>
            <input
              className="input"
              value={deletePhrase}
              onChange={(e) => setDeletePhrase(e.target.value)}
              placeholder="DELETE"
              autoFocus
            />
            <div className="flex gap-2">
              <button
                onClick={deleteAccount}
                disabled={deleting || deletePhrase !== 'DELETE'}
                className="btn-primary bg-rose-600 hover:bg-rose-700 disabled:opacity-50"
              >
                {deleting ? 'Deleting...' : 'Permanently delete'}
              </button>
              <button
                onClick={() => {
                  setDeleteOpen(false);
                  setDeletePhrase('');
                }}
                disabled={deleting}
                className="btn-ghost"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
